import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const effectiveDate = new Date().toLocaleDateString('en-IN', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Navigation */}
      <nav className="border-b border-stone-100 bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-18 py-4">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
              <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <span 
                className="font-semibold text-xl text-stone-900 tracking-tight"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                ClarityStack
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Features
              </Link>
              <Link to="/pricing" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Pricing
              </Link>
              <Link to="/contact" className="text-sm font-medium text-stone-600 hover:text-[#2F5233] transition-colors">
                Contact
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-stone-600 hover:text-stone-900 font-medium"
              >
                Login
              </Button>
              <Button 
                className="bg-[#2F5233] hover:bg-[#243f28] text-white rounded-full px-6 font-medium shadow-sm"
                onClick={() => navigate('/register')}
              >
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="py-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 
            className="text-4xl font-semibold text-stone-900 mb-3"
            style={{ fontFamily: 'Fraunces, Georgia, serif' }}
          >
            Privacy Policy
          </h1>
          <p className="text-sm text-stone-400 mb-12">Effective Date: {effectiveDate}</p>

          <div className="prose prose-stone max-w-none">
            <p className="text-stone-600 text-lg leading-relaxed mb-8">
              ClarityStack is a software platform operated by <strong className="text-stone-900">Think Through Enterprise</strong> that provides administrative and documentation tools for therapists and coaches. We do not provide medical advice, diagnosis, or treatment.
            </p>

            <h2 
              className="text-2xl font-semibold text-stone-900 mt-12 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Data Collection
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              We collect therapist account details and data entered by therapists, including session notes and patient information. We do not collect patient data directly from patients.
            </p>

            <h2 
              className="text-2xl font-semibold text-stone-900 mt-12 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Data Usage
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              Data is used solely to provide scheduling, documentation, and workflow assistance. We do not sell or share user data with third parties for marketing.
            </p>

            <h2 
              className="text-2xl font-semibold text-stone-900 mt-12 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              AI Processing
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              AI features assist in summarisation and structuring of notes. All AI output is editable and controlled by the therapist.
            </p>

            <h2 
              className="text-2xl font-semibold text-stone-900 mt-12 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Data Security
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              We implement industry-standard security practices and encryption. Audio uploads, if used, are processed temporarily and deleted after transcription.
            </p>

            <h2 
              className="text-2xl font-semibold text-stone-900 mt-12 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Consent Responsibility
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              Therapists are responsible for obtaining patient consent before using AI or communication tools.
            </p>

            <h2 
              className="text-2xl font-semibold text-stone-900 mt-12 mb-4"
              style={{ fontFamily: 'Fraunces, Georgia, serif' }}
            >
              Contact
            </h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              For privacy-related inquiries, please contact us at{' '}
              <a href="mailto:support@claritystack.live" className="text-[#2F5233] hover:underline font-medium">
                support@claritystack.live
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-16 px-6 lg:px-8 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-xl bg-[#2F5233] flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <span 
                  className="font-semibold text-xl tracking-tight"
                  style={{ fontFamily: 'Fraunces, Georgia, serif' }}
                >
                  ClarityStack
                </span>
              </div>
              <p className="text-stone-400 text-sm mb-6">
                ClarityStack is a product of Think Through Enterprise
              </p>
              <div className="text-sm text-stone-400 space-y-2">
                <p className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-stone-500" />
                  <span>B104 Shantideep Society, Sathya Sai Complex,<br />Chikuwadi, Borivali West, Mumbai – 400092</span>
                </p>
                <p className="flex items-center gap-3">
                  <Phone className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>+91 9967124157</span>
                </p>
                <p className="flex items-center gap-3">
                  <Mail className="h-4 w-4 flex-shrink-0 text-stone-500" />
                  <span>support@claritystack.live</span>
                </p>
              </div>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/#features" className="text-stone-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="text-stone-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/contact" className="text-stone-400 hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm">
                <li><Link to="/privacy" className="text-stone-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-stone-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/refund" className="text-stone-400 hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-stone-800">
            <p className="text-sm text-stone-500 text-center">
              © {new Date().getFullYear()} Think Through Enterprise. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
